import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';
import IconSelector from '../components/IconSelector';

interface PromotionFormData {
  title: string;
  description: string;
  discount_percent: string;
  discount_value: string;
  start_date: string;
  end_date: string;
  active: boolean;
  imageType: 'image' | 'icon' | 'none';
  image: File | null;
  icon: string;
  // Novos campos
  target_scope: 'all' | 'single' | 'multiple';
  promotion_type: 'shipping' | 'total' | 'item' | 'minimum';
  selected_products: number[];
}

function PromotionForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useNotification();
  
  const promotionId = searchParams.get('edit');
  const isNew = searchParams.get('new') === 'true';
  const isEdit = !!promotionId;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PromotionFormData>({
    title: '',
    description: '',
    discount_percent: '',
    discount_value: '',
    start_date: '',
    end_date: '',
    active: true,
    imageType: 'none',
    image: null,
    icon: '',
    target_scope: 'all',
    promotion_type: 'item',
    selected_products: [],
  });
  const [imagePreview, setImagePreview] = useState<string>('');
  const [products, setProducts] = useState<any[]>([]);
  const [showProductSelector, setShowProductSelector] = useState(false);

  useEffect(() => {
    if (isEdit && promotionId) {
      loadPromotion(parseInt(promotionId));
    }
    // Carregar produtos para seleção
    loadProducts();
  }, [isEdit, promotionId]);

  const loadProducts = async () => {
    try {
      const productsData = await AdminAPI.get('products') as any[];
      setProducts(productsData);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    }
  };

  const loadPromotion = async (id: number) => {
    try {
      const promotion = await AdminAPI.get(`promotions/${id}`) as any;
      
      // Determinar tipo de imagem/ícone
      let imageType: 'image' | 'icon' | 'none' = 'none';
      let imagePreviewUrl = '';
      let iconName = '';
      
      if (promotion.image) {
        // Verificar se é um ícone (começa com "fa-") ou imagem
        if (typeof promotion.image === 'string' && promotion.image.startsWith('fa-')) {
          imageType = 'icon';
          iconName = promotion.image;
        } else {
          imageType = 'image';
          imagePreviewUrl = promotion.image;
        }
      }
      
      setFormData({
        title: promotion.title || '',
        description: promotion.description || '',
        discount_percent: String(promotion.discount_percent || ''),
        discount_value: String(promotion.discount_value || ''),
        start_date: promotion.start_date ? promotion.start_date.split('T')[0] : '',
        end_date: promotion.end_date ? promotion.end_date.split('T')[0] : '',
        active: promotion.active !== false,
        imageType,
        image: null,
        icon: iconName,
        target_scope: promotion.target_scope || 'all',
        promotion_type: promotion.promotion_type || 'item',
        selected_products: promotion.product_ids || [],
      });
      
      setImagePreview(imagePreviewUrl);
    } catch (err) {
      console.error('Erro ao carregar promoção:', err);
      error('Erro ao carregar promoção');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageTypeChange = (type: 'image' | 'icon' | 'none') => {
    setFormData((prev) => ({ ...prev, imageType: type }));
    if (type !== 'image') {
      setImagePreview('');
      setFormData((prev) => ({ ...prev, image: null }));
    }
    if (type !== 'icon') {
      setFormData((prev) => ({ ...prev, icon: '' }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
      e.target.value = '';
    }
  };

  const handleIconSelect = (icon: string) => {
    setFormData((prev) => ({ ...prev, icon }));
  };

  const removeImage = () => {
    if (imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setFormData((prev) => ({ ...prev, image: null }));
    setImagePreview('');
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validar que pelo menos um desconto foi preenchido
      if (!formData.discount_percent && !formData.discount_value) {
        error('Preencha pelo menos um tipo de desconto (percentual ou valor)');
        setLoading(false);
        return;
      }

      // Validar seleção de produtos quando necessário
      if ((formData.target_scope === 'single' || formData.target_scope === 'multiple') && formData.selected_products.length === 0) {
        error('Selecione pelo menos um produto');
        setLoading(false);
        return;
      }

      if (formData.target_scope === 'single' && formData.selected_products.length > 1) {
        error('Selecione apenas um produto');
        setLoading(false);
        return;
      }

      let imageData = null;
      
      if (formData.imageType === 'image' && formData.image) {
        // Converter imagem para base64
        imageData = await fileToBase64(formData.image);
      } else if (formData.imageType === 'image' && imagePreview && !imagePreview.startsWith('blob:')) {
        // Manter imagem existente
        imageData = imagePreview;
      } else if (formData.imageType === 'icon' && formData.icon) {
        // Usar ícone (salvar como string "fa-...")
        imageData = formData.icon;
      }

      const promotionData = {
        title: formData.title,
        description: formData.description,
        discount_percent: formData.discount_percent ? parseInt(formData.discount_percent) : null,
        discount_value: formData.discount_value ? parseFloat(formData.discount_value) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        active: formData.active,
        image: imageData,
        target_scope: formData.target_scope,
        promotion_type: formData.promotion_type,
        product_ids: formData.target_scope === 'all' ? [] : formData.selected_products,
        conditions_data: {}, // Pode ser usado para condições futuras
      };

      let result;
      if (isEdit && promotionId) {
        result = await AdminAPI.put(`promotions/${promotionId}`, promotionData);
        success('Promoção atualizada com sucesso!');
      } else {
        result = await AdminAPI.post('promotions', promotionData);
        success('Promoção criada com sucesso!');
      }

      setTimeout(() => {
        navigate('/admin/promocoes');
      }, 1000);
    } catch (err: any) {
      console.error('Erro ao salvar promoção:', err);
      error(err.message || 'Erro ao salvar promoção');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-content">
      <form className="product-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="title">
            Título da Promoção *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            className="form-input"
            value={formData.title}
            onChange={handleInputChange}
            required
            placeholder="Ex: Black Friday"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="description">
            Descrição
          </label>
          <textarea
            id="description"
            name="description"
            className="form-textarea"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Descreva a promoção..."
          />
        </div>

        {/* Escopo da Promoção */}
        <div className="form-group">
          <label className="form-label">
            Escopo da Promoção *
          </label>
          <div className="image-icon-toggle">
            <button
              type="button"
              className={`toggle-option ${formData.target_scope === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFormData(prev => ({ ...prev, target_scope: 'all', selected_products: [] }));
                setShowProductSelector(false);
              }}
            >
              <i className="fas fa-boxes"></i>
              <span>Todos os Produtos</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.target_scope === 'single' ? 'active' : ''}`}
              onClick={() => {
                setFormData(prev => ({ ...prev, target_scope: 'single', selected_products: prev.selected_products.slice(0, 1) }));
                setShowProductSelector(true);
              }}
            >
              <i className="fas fa-box"></i>
              <span>Um Único Produto</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.target_scope === 'multiple' ? 'active' : ''}`}
              onClick={() => {
                setFormData(prev => ({ ...prev, target_scope: 'multiple' }));
                setShowProductSelector(true);
              }}
            >
              <i className="fas fa-layer-group"></i>
              <span>Produtos Múltiplos</span>
            </button>
          </div>
        </div>

        {/* Seleção de Produtos (quando não for "todos") */}
        {(formData.target_scope === 'single' || formData.target_scope === 'multiple') && (
          <div className="form-group">
            <label className="form-label">
              {formData.target_scope === 'single' ? 'Selecionar Produto *' : 'Selecionar Produtos *'}
            </label>
            <div className="product-selector-container">
              <div className="selected-products-list">
                {formData.selected_products.map(productId => {
                  const product = products.find(p => p.id === productId);
                  return product ? (
                    <div key={productId} className="selected-product-chip">
                      <span>{product.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            selected_products: prev.selected_products.filter(id => id !== productId)
                          }));
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowProductSelector(!showProductSelector)}
                style={{ marginTop: '8px' }}
              >
                {showProductSelector ? 'Fechar Lista' : 'Adicionar Produtos'}
              </button>
              
              {showProductSelector && (
                <div className="product-selector-dropdown">
                  {products.filter(p => !formData.selected_products.includes(p.id)).map(product => (
                    <button
                      key={product.id}
                      type="button"
                      className="product-selector-item"
                      onClick={() => {
                        if (formData.target_scope === 'single') {
                          setFormData(prev => ({ ...prev, selected_products: [product.id] }));
                          setShowProductSelector(false);
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            selected_products: [...prev.selected_products, product.id]
                          }));
                        }
                      }}
                    >
                      {product.name}
                    </button>
                  ))}
                  {products.filter(p => !formData.selected_products.includes(p.id)).length === 0 && (
                    <div className="product-selector-empty">Todos os produtos já foram selecionados</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tipo de Desconto */}
        <div className="form-group">
          <label className="form-label">
            Tipo de Desconto *
          </label>
          <div className="image-icon-toggle">
            <button
              type="button"
              className={`toggle-option ${formData.promotion_type === 'shipping' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, promotion_type: 'shipping' }))}
            >
              <i className="fas fa-truck"></i>
              <span>Frete</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.promotion_type === 'total' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, promotion_type: 'total' }))}
            >
              <i className="fas fa-receipt"></i>
              <span>Valor Final</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.promotion_type === 'item' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, promotion_type: 'item' }))}
            >
              <i className="fas fa-tag"></i>
              <span>Valor do Item</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.promotion_type === 'minimum' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, promotion_type: 'minimum' }))}
            >
              <i className="fas fa-shopping-cart"></i>
              <span>Valor Mínimo</span>
            </button>
          </div>
        </div>

        {/* Campos de Desconto */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="discount_percent">
              Desconto Percentual (%)
            </label>
            <input
              type="number"
              id="discount_percent"
              name="discount_percent"
              className="form-input"
              value={formData.discount_percent}
              onChange={handleInputChange}
              min="0"
              max="100"
              placeholder="Ex: 20"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="discount_value">
              Desconto em Valor (R$)
            </label>
            <input
              type="number"
              step="0.01"
              id="discount_value"
              name="discount_value"
              className="form-input"
              value={formData.discount_value}
              onChange={handleInputChange}
              min="0"
              placeholder="Ex: 50.00"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="start_date">
              Data de Início
            </label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              className="form-input"
              value={formData.start_date}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="end_date">
              Data de Término
            </label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              className="form-input"
              value={formData.end_date}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Seleção de Imagem ou Ícone */}
        <div className="form-group">
          <label className="form-label">
            Imagem ou Ícone (Opcional)
          </label>
          
          <div className="image-icon-toggle">
            <button
              type="button"
              className={`toggle-option ${formData.imageType === 'none' ? 'active' : ''}`}
              onClick={() => handleImageTypeChange('none')}
            >
              <i className="fas fa-ban"></i>
              <span>Nenhum</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.imageType === 'image' ? 'active' : ''}`}
              onClick={() => handleImageTypeChange('image')}
            >
              <i className="fas fa-image"></i>
              <span>Imagem</span>
            </button>
            <button
              type="button"
              className={`toggle-option ${formData.imageType === 'icon' ? 'active' : ''}`}
              onClick={() => handleImageTypeChange('icon')}
            >
              <i className="fas fa-icons"></i>
              <span>Ícone</span>
            </button>
          </div>

          {/* Upload de Imagem */}
          {formData.imageType === 'image' && (
            <>
              <div className="image-upload">
                <input
                  type="file"
                  id="promotion-image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <label htmlFor="promotion-image" style={{ cursor: 'pointer' }}>
                  <i className="fas fa-camera" style={{ fontSize: '32px', color: 'var(--text-secondary)', marginBottom: '8px' }}></i>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Clique para adicionar imagem
                  </div>
                </label>
              </div>

              {imagePreview && (
                <div className="image-preview" style={{ marginTop: '12px' }}>
                  <div className="image-preview-item">
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={removeImage}
                      aria-label="Remover imagem"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Seletor de Ícone */}
          {formData.imageType === 'icon' && (
            <>
              <IconSelector
                selectedIcon={formData.icon}
                onIconSelect={handleIconSelect}
              />
              {formData.icon && (
                <div className="icon-preview" style={{ marginTop: '16px', padding: '16px', background: 'var(--background)', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>
                    <i className={`fas ${formData.icon}`} style={{ fontSize: '48px', color: 'var(--secondary)' }}></i>
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Ícone selecionado: <strong>{formData.icon}</strong>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-group">
          <label className="form-checkbox">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleInputChange}
            />
            <span>Promoção ativa</span>
          </label>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/promocoes')}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : isEdit ? 'Atualizar' : 'Criar'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PromotionForm;

